import {
    DataGridPro,
    GridColDef,
    GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { IconButton, Button, Link, TextField } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import DashboardLoadingDataView from '@pages/Dashboard/components/DashboardLoadingDataView'
import useDataGridSearch from '@hooks/useDataGridSearch'
import SelectedRowDetailsDialog from '@components/SelectedRowDetailsDialog'
import useOrgCollectionPage from './useOrgCollectionsPage'
import { Link as WouterLink } from 'wouter'

const columns = ({
    handleDelete,
}: {
    handleDelete: (id: string) => void
}): GridColDef[] => {
    return [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
        },
        {
            field: 'admins',
            headerName: 'Admins',
            width: 120,
            renderCell: (params: GridRenderCellParams) => {
                const count = params.row.orgUsers.filter(
                    (user: { role: string }) => {
                        // The ATSP admin role is `atsp`, not
                        // `atsp-admin` — the wrong name left them out
                        // of this count entirely.
                        return user.role === 'admin' || user.role === 'atsp'
                    }
                ).length
                return <>{count}</>
            },
        },
        {
            field: 'edit',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Link
                        underline="hover"
                        href={`/edit-org-collection/${params.row.id}`}
                        component={WouterLink}
                    >
                        <IconButton size="small">
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Link>
                )
            },
        },
        {
            field: 'delete',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <IconButton
                        size="small"
                        onClick={() => {
                            const shouldDelete = confirm(
                                `Are you sure you want to delete collection: ${params.row.name}?`
                            )
                            if (!shouldDelete) {
                                return
                            }
                            handleDelete(params.row.id)
                        }}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                )
            },
        },
    ]
}

export default function OrgCollectionsOverviewPage() {
    const { loading, error, rows, handleDelete } = useOrgCollectionPage()

    const { searchText, requestSearch, filterModel } = useDataGridSearch()

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (loading) {
        return <DashboardLoadingDataView loading={loading} />
    }

    return (
        <DashboardLayout loading={loading}>
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                }}
            >
                <TextField
                    variant="standard"
                    value={searchText}
                    onChange={(event) => requestSearch(event.target.value)}
                    placeholder="Search…"
                />
                <div
                    style={{
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            color: 'var(--grey-700)',
                            fontSize: '0.875rem',
                        }}
                    >
                        <strong>{rows.length} results</strong>
                    </p>
                </div>
                <div>
                    <Link underline="hover" href={'/create-org-collection'}>
                        <Button variant="outlined">
                            Create New Collection
                        </Button>
                    </Link>
                </div>
            </div>

            <SelectedRowDetailsDialog onClose={() => {}} selectedRow={null} />

            <DataGridPro
                rows={rows}
                columns={columns({ handleDelete })}
                pageSizeOptions={[10, 20, 50, { value: -1, label: 'All' }]}
                pagination
                initialState={{
                    filter: {
                        filterModel,
                    },
                }}
                filterModel={filterModel}
            />
        </DashboardLayout>
    )
}
